import React from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    IconButton,
    Grid,
    Paper,
    Button,
    FormHelperText,
    Avatar,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ClearIcon from '@mui/icons-material/Clear';
import SponsorTierForm from '../../../../../components/onboardingForms/SponsorTierForm';

const PRICING_STRATEGIES = {
    // Simple per-unit pricing
    per_guest: { label: 'Per Guest', unitLabel: 'per guest', example: '95' },
    per_tray: { label: 'Per Tray', unitLabel: 'per tray', example: '40' },
    per_hour: { label: 'Per Hour', unitLabel: 'per hour', example: '150' },
    per_photo: { label: 'Per Photo', unitLabel: 'per photo', example: '25' },
    per_deliverable: { 
        label: 'Per Deliverable', 
        unitLabel: 'per deliverable', 
        example: '500',
        requiresDescription: true 
    },
    per_staff_member: { label: 'Per Staff Member', unitLabel: 'per staff member per hour', example: '30' },
    per_equipment_piece: { label: 'Per Equipment Piece', unitLabel: 'per equipment item', example: '100' },
    per_day: { label: 'Per Day', unitLabel: 'per day', example: '1200' },
    per_shift: { label: 'Per Shift', unitLabel: 'per shift', example: '200' },
    per_team: { label: 'Per Team', unitLabel: 'per team', example: '800' },
    
    // Complex pricing strategies
    tiered_per_person: { label: 'Tiered Per Person', type: 'tiered' },
    combo: { label: 'Package Bundle', type: 'combo' },
    per_menu_item: { label: 'Per Menu Item', type: 'menu' },
    per_event: { label: 'Per Event (Flat Fee)', type: 'flat' },
    sponsor: { label: 'Sponsorship Tiers', type: 'sponsor' }
};

const SPONSOR_TIERS = [
    { value: 'title', label: 'Title Sponsor' },
    { value: 'gold', label: 'Gold Tier' },
    { value: 'silver', label: 'Silver Tier' },
    { value: 'bronze', label: 'Bronze Tier' },
    { value: 'in_kind', label: 'In-Kind Sponsor' }
];

const MONETARY_RANGES = [
    { value: 'up_to_1000', label: 'Up to $1,000' },
    { value: '1000_3000', label: '$1,000 - $3,000' },
    { value: '3000_5000', label: '$3,000 - $5,000' },
    { value: '5000_10000', label: '$5,000 - $10,000' },
    { value: '10000_25000', label: '$10,000 - $25,000' },
    { value: '25000_50000', label: '$25,000 - $50,000' },
    { value: '50000_plus', label: '$50,000+' },
    { value: 'custom', label: 'Custom Amount' }
];

const TieredPricingForm = ({ tiers = [], onUpdate, errors }) => {
    const handleAddTier = () => {
        onUpdate([...tiers, { name: '', minGuests: '', maxGuests: '', pricePerGuest: '' }]);
    };

    const handleRemoveTier = (index) => {
        const newTiers = tiers.filter((_, i) => i !== index);
        onUpdate(newTiers);
    };

    const handleTierChange = (index, field, value) => {
        const newTiers = tiers.map((tier, i) => {
            if (i === index) {
                return { ...tier, [field]: value };
            }
            return tier;
        });
        onUpdate(newTiers);
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Guest Tiers</Typography>
            {tiers.map((tier, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Tier Name"
                                placeholder="e.g., Basic, Standard, Premium"
                                value={tier.name}
                                onChange={(e) => handleTierChange(index, 'name', e.target.value)}
                                error={errors?.tiers?.[index]?.name}
                                helperText={errors?.tiers?.[index]?.name}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                label="Min Guests"
                                type="number"
                                value={tier.minGuests}
                                onChange={(e) => handleTierChange(index, 'minGuests', e.target.value)}
                                error={errors?.tiers?.[index]?.minGuests}
                                helperText={errors?.tiers?.[index]?.minGuests}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                label="Max Guests"
                                type="number"
                                value={tier.maxGuests}
                                onChange={(e) => handleTierChange(index, 'maxGuests', e.target.value)}
                                error={errors?.tiers?.[index]?.maxGuests}
                                helperText={errors?.tiers?.[index]?.maxGuests}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Price Per Guest ($)"
                                type="number"
                                value={tier.pricePerGuest}
                                onChange={(e) => handleTierChange(index, 'pricePerGuest', e.target.value)}
                                error={errors?.tiers?.[index]?.pricePerGuest}
                                helperText={errors?.tiers?.[index]?.pricePerGuest}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <IconButton onClick={() => handleRemoveTier(index)} color="error">
                                <DeleteIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
            <Button
                startIcon={<AddIcon />}
                onClick={handleAddTier}
                variant="outlined"
                sx={{ mt: 1 }}
            >
                Add Tier
            </Button>
        </Box>
    );
};

const MenuItemsForm = ({ items = [], onUpdate, errors }) => {
    const handleAddItem = () => {
        onUpdate([...items, { name: '', price: '', image: null }]);
    };

    const handleRemoveItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        onUpdate(newItems);
    };

    const handleItemChange = (index, field, value) => {
        const newItems = items.map((item, i) => {
            if (i === index) {
                return { ...item, [field]: value };
            }
            return item;
        });
        onUpdate(newItems);
    };

    const handleImageUpload = (index, event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                handleItemChange(index, 'image', reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = (index) => {
        handleItemChange(index, 'image', null);
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Menu Items</Typography>
            {items.map((item, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Item Name"
                                value={item.name}
                                onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                error={errors?.items?.[index]?.name}
                                helperText={errors?.items?.[index]?.name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Price ($)"
                                type="number"
                                value={item.price}
                                onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                error={errors?.items?.[index]?.price}
                                helperText={errors?.items?.[index]?.price}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <IconButton onClick={() => handleRemoveItem(index)} color="error">
                                <DeleteIcon />
                            </IconButton>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {item.image ? (
                                    <Box sx={{ position: 'relative' }}>
                                        <Avatar
                                            src={item.image}
                                            variant="rounded"
                                            sx={{ width: 100, height: 100 }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -8,
                                                right: -8,
                                                bgcolor: 'background.paper'
                                            }}
                                            onClick={() => handleRemoveImage(index)}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        startIcon={<AddPhotoAlternateIcon />}
                                        size="small"
                                    >
                                        Add Image
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            onChange={(e) => handleImageUpload(index, e)}
                                        />
                                    </Button>
                                )}
                                <Typography variant="caption" color="textSecondary">
                                    Optional: Add an image of the menu item
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
            <Button
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                variant="outlined"
                sx={{ mt: 1 }}
            >
                Add Menu Item
            </Button>
        </Box>
    );
};

const ComboPackageForm = ({ packages = [], onUpdate, errors }) => {
    const handleAddPackage = () => {
        onUpdate([...packages, { name: '', description: '', price: '' }]);
    };

    const handleRemovePackage = (index) => {
        const newPackages = packages.filter((_, i) => i !== index);
        onUpdate(newPackages);
    };

    const handlePackageChange = (index, field, value) => {
        const newPackages = packages.map((pkg, i) => {
            if (i === index) {
                return { ...pkg, [field]: value };
            }
            return pkg;
        });
        onUpdate(newPackages);
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Package Bundles</Typography>
            {packages.map((pkg, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Package Name"
                                value={pkg.name}
                                onChange={(e) => handlePackageChange(index, 'name', e.target.value)}
                                error={errors?.packages?.[index]?.name}
                                helperText={errors?.packages?.[index]?.name}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={2}
                                value={pkg.description}
                                onChange={(e) => handlePackageChange(index, 'description', e.target.value)}
                                error={errors?.packages?.[index]?.description}
                                helperText={errors?.packages?.[index]?.description}
                            />
                        </Grid>
                        <Grid item xs={10}>
                            <TextField
                                fullWidth
                                label="Price ($)"
                                type="number"
                                value={pkg.price}
                                onChange={(e) => handlePackageChange(index, 'price', e.target.value)}
                                error={errors?.packages?.[index]?.price}
                                helperText={errors?.packages?.[index]?.price}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <IconButton onClick={() => handleRemovePackage(index)} color="error">
                                <DeleteIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
            <Button
                startIcon={<AddIcon />}
                onClick={handleAddPackage}
                variant="outlined"
                sx={{ mt: 1 }}
            >
                Add Package Bundle
            </Button>
        </Box>
    );
};

const FlatFeeForm = ({ data, onUpdate, errors }) => {
    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Flat Fee ($)"
                        type="number"
                        value={data.flatFee || ''}
                        onChange={(e) => onUpdate({ ...data, flatFee: e.target.value })}
                        error={!!errors?.flatFee}
                        helperText={errors?.flatFee}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Duration/Guest Cap Notes"
                        multiline
                        rows={2}
                        value={data.notes || ''}
                        onChange={(e) => onUpdate({ ...data, notes: e.target.value })}
                        error={!!errors?.notes}
                        helperText={errors?.notes}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

const PricingStep = ({ packageData, onFieldChange, errors }) => {
    const handlePricingDataChange = (field, value) => {
        onFieldChange('pricingData', {
            ...packageData.pricingData,
            [field]: value
        });
    };

    const renderPricingForm = () => {
        const strategy = packageData.pricingStrategy;
        const data = packageData.pricingData || {};

        if (strategy === 'sponsor') {
            return (
                <SponsorTierForm
                    tiers={data.tiers || []}
                    onTiersChange={(tiers) => handlePricingDataChange('tiers', tiers)}
                />
            );
        }

        const strategyInfo = PRICING_STRATEGIES[strategy];

        if (strategyInfo.type === 'tiered') {
            return <TieredPricingForm 
                tiers={data.tiers || []}
                onUpdate={(tiers) => handlePricingDataChange('tiers', tiers)}
                errors={errors?.pricingData}
            />;
        }

        if (strategyInfo.type === 'menu') {
            return <MenuItemsForm 
                items={data.items || []}
                onUpdate={(items) => handlePricingDataChange('items', items)}
                errors={errors?.pricingData}
            />;
        }

        if (strategyInfo.type === 'combo') {
            return <ComboPackageForm 
                packages={data.packages || []}
                onUpdate={(packages) => handlePricingDataChange('packages', packages)}
                errors={errors?.pricingData}
            />;
        }

        if (strategyInfo.type === 'flat') {
            return <FlatFeeForm 
                data={data}
                onUpdate={(newData) => handlePricingDataChange('flatFee', newData.flatFee)}
                errors={errors?.pricingData}
            />;
        }

        // Simple per-unit pricing
        return (
            <Box>
                <TextField
                    fullWidth
                    label={`Price ${strategyInfo.unitLabel} ($)`}
                    type="number"
                    value={data.price || ''}
                    onChange={(e) => handlePricingDataChange('price', e.target.value)}
                    error={!!errors?.pricingData?.price}
                    helperText={errors?.pricingData?.price || `Example: $${strategyInfo.example}`}
                    sx={{ mb: strategyInfo.requiresDescription ? 2 : 0 }}
                />
                {strategyInfo.requiresDescription && (
                    <TextField
                        fullWidth
                        label="Deliverable Description"
                        multiline
                        rows={2}
                        value={data.description || ''}
                        onChange={(e) => handlePricingDataChange('description', e.target.value)}
                        error={!!errors?.pricingData?.description}
                        helperText={errors?.pricingData?.description || "Describe what is included in each deliverable"}
                    />
                )}
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Pricing Strategy
            </Typography>
            <FormControl fullWidth error={!!errors?.pricingStrategy} sx={{ mb: 3 }}>
                <InputLabel>Select Pricing Strategy</InputLabel>
                <Select
                    value={packageData.pricingStrategy || ''}
                    onChange={(e) => onFieldChange('pricingStrategy', e.target.value)}
                    label="Select Pricing Strategy"
                >
                    {Object.entries(PRICING_STRATEGIES).map(([key, { label }]) => (
                        <MenuItem key={key} value={key}>
                            {label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {packageData.pricingStrategy && renderPricingForm()}
        </Box>
    );
};

export default PricingStep; 